import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel, type MemberKind } from './contexts';
import { type Db, must, useDb } from './db';

export interface PersonRow {
  id: string;
  kind: MemberKind;
  lastName: string;
  firstName: string;
  middleName: string | null;
  birthDate: string | null;
  hasAccount: boolean;
  classLabel: string | null;
  roles: string[];
}

/** Everybody in the school (administration view). */
export function usePeople(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['admin', 'people', schoolId],
    queryFn: async (): Promise<PersonRow[]> => {
      const [people, enrollments, roles] = await Promise.all([
        db
          .from('school_members')
          .select('id, kind, last_name, first_name, middle_name, birth_date, user_id, status')
          .eq('school_id', schoolId)
          .eq('status', 'active')
          .order('last_name'),
        db
          .from('class_enrollments')
          .select(
            'student_member_id, class:classes!class_enrollments_school_id_class_id_fkey(grade, letter)',
          )
          .eq('school_id', schoolId),
        db.from('staff_roles').select('member_id, role').eq('school_id', schoolId),
      ]);
      const classOf = new Map(
        must(enrollments).map((e) => [e.student_member_id, classLabel(e.class)]),
      );
      const roleList = must(roles);
      return must(people).map((p) => ({
        id: p.id,
        kind: p.kind,
        lastName: p.last_name,
        firstName: p.first_name,
        middleName: p.middle_name,
        birthDate: p.birth_date,
        hasAccount: p.user_id !== null,
        classLabel: classOf.get(p.id) ?? null,
        roles: roleList.filter((r) => r.member_id === p.id).map((r) => r.role),
      }));
    },
  });
}

export interface NewPerson {
  kind: MemberKind;
  lastName: string;
  firstName: string;
  middleName?: string;
  birthDate?: string;
  /** students: the class to enroll into */
  classId?: string;
  /** guardians: the child */
  childId?: string;
}

export async function addPerson(db: Db, schoolId: string, person: NewPerson): Promise<string> {
  const { data, error } = await db
    .from('school_members')
    .insert({
      school_id: schoolId,
      kind: person.kind,
      last_name: person.lastName.trim(),
      first_name: person.firstName.trim(),
      middle_name: person.middleName?.trim() || null,
      birth_date: person.kind === 'student' ? person.birthDate || null : null,
    })
    .select('id')
    .single();
  if (error) throw error;
  if (person.kind === 'student' && person.classId) {
    must(await db.rpc('enroll_student', { p_student: data.id, p_class: person.classId }));
  }
  if (person.kind === 'guardian' && person.childId) {
    must(
      await db.from('guardianships').insert({
        school_id: schoolId,
        guardian_member_id: data.id,
        student_member_id: person.childId,
      }),
    );
  }
  return data.id;
}

/** «тип;прізвище;імʼя;по батькові;дата народження;клас» — one person per line. */
export interface CsvResult {
  added: number;
  errors: Array<{ line: number; message: string }>;
}

const KINDS: Record<string, MemberKind> = {
  учень: 'student',
  учениця: 'student',
  student: 'student',
  батьки: 'guardian',
  мама: 'guardian',
  тато: 'guardian',
  guardian: 'guardian',
  працівник: 'staff',
  вчитель: 'staff',
  staff: 'staff',
};

export function parsePeopleCsv(
  text: string,
  classes: ReadonlyArray<{ id: string; label: string }>,
): { people: Array<NewPerson & { line: number }>; errors: CsvResult['errors'] } {
  const people: Array<NewPerson & { line: number }> = [];
  const errors: CsvResult['errors'] = [];
  text.split(/\r?\n/).forEach((raw, index) => {
    const line = index + 1;
    if (!raw.trim() || (index === 0 && /прізвище/i.test(raw))) return;
    const cells = raw.split(/[;,\t]/).map((c) => c.trim());
    const kind = KINDS[(cells[0] ?? '').toLowerCase()];
    const [, lastName = '', firstName = '', middleName = '', birthDate = '', klass = ''] = cells;
    if (!kind) return errors.push({ line, message: 'unknown_kind' });
    if (!lastName || !firstName) return errors.push({ line, message: 'name_missing' });
    if (birthDate && !/^\d{4}-\d{2}-\d{2}$/.test(birthDate))
      return errors.push({ line, message: 'bad_date' });
    const classId = klass
      ? classes.find((c) => c.label.toLowerCase() === klass.toLowerCase())?.id
      : undefined;
    if (klass && !classId) return errors.push({ line, message: 'unknown_class' });
    people.push({
      line,
      kind,
      lastName,
      firstName,
      ...(middleName && { middleName }),
      ...(birthDate && { birthDate }),
      ...(classId && { classId }),
    });
    return undefined;
  });
  return { people, errors };
}

export function useAdminActions(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['admin'] });
  return {
    add: useMutation({
      mutationFn: (p: NewPerson) => addPerson(db, schoolId, p),
      onSettled: invalidate,
    }),
    importCsv: useMutation({
      mutationFn: async (people: Array<NewPerson & { line: number }>): Promise<CsvResult> => {
        const result: CsvResult = { added: 0, errors: [] };
        for (const p of people) {
          try {
            await addPerson(db, schoolId, p);
            result.added += 1;
          } catch (error) {
            result.errors.push({ line: p.line, message: (error as Error).message });
          }
        }
        return result;
      },
      onSettled: invalidate,
    }),
    invite: useMutation({
      mutationFn: async (memberId: string) => {
        const rows = must(await db.rpc('create_invite', { p_member_id: memberId }));
        return rows[0] as { invite_code: string; valid_until: string };
      },
    }),
    updateClass: useMutation({
      mutationFn: async (a: {
        classId: string;
        homeroomId?: string | null;
        shelterId?: string | null;
      }) =>
        must(
          await db
            .from('classes')
            .update({
              ...(a.homeroomId !== undefined && { homeroom_member_id: a.homeroomId }),
              ...(a.shelterId !== undefined && { shelter_id: a.shelterId }),
            })
            .eq('id', a.classId),
        ),
      onSettled: () => client.invalidateQueries({ queryKey: ['classes'] }),
    }),
    addSlot: useMutation({
      mutationFn: async (a: {
        termId: string;
        courseId: string;
        weekday: number;
        period: number;
        room?: string;
      }) =>
        must(
          await db.from('timetable_slots').insert({
            school_id: schoolId,
            term_id: a.termId,
            course_id: a.courseId,
            weekday: a.weekday,
            period: a.period,
            room: a.room || null,
          }),
        ),
      onSettled: () => client.invalidateQueries({ queryKey: ['admin', 'timetable'] }),
    }),
    deleteSlot: useMutation({
      mutationFn: async (id: string) =>
        must(await db.from('timetable_slots').delete().eq('id', id)),
      onSettled: () => client.invalidateQueries({ queryKey: ['admin', 'timetable'] }),
    }),
    generateLessons: useMutation({
      mutationFn: async (termId: string) =>
        must(await db.rpc('generate_lessons', { p_term: termId })) as number,
      onSettled: () => client.invalidateQueries({ queryKey: ['lessons'] }),
    }),
  };
}

export function useShelters(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['shelters', schoolId],
    queryFn: async () =>
      must(
        await db
          .from('shelters')
          .select('id, name, capacity')
          .eq('school_id', schoolId)
          .order('name'),
      ),
  });
}

/** The weekly template of a class in a term. */
export function useClassTimetable(classId: string | undefined, termId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['admin', 'timetable', classId, termId],
    enabled: classId !== undefined && termId !== undefined,
    queryFn: async () => {
      const courses = must(
        await db
          .from('courses')
          .select('id, group_label, subject:subjects!courses_school_id_subject_id_fkey(name)')
          .eq('class_id', classId as string),
      );
      const slots = must(
        await db
          .from('timetable_slots')
          .select('id, course_id, weekday, period, room')
          .eq('term_id', termId as string)
          .in(
            'course_id',
            courses.map((c) => c.id),
          ),
      );
      const label = (id: string) => {
        const c = courses.find((x) => x.id === id);
        return c ? `${c.subject.name}${c.group_label ? ` (${c.group_label})` : ''}` : '';
      };
      return {
        courses: courses
          .map((c) => ({ id: c.id, label: label(c.id) }))
          .sort((a, b) => a.label.localeCompare(b.label, 'uk')),
        slots: slots.map((s) => ({ ...s, label: label(s.course_id) })),
      };
    },
  });
}

export interface AuditRow {
  id: number;
  at: string;
  actor_name: string | null;
  action: string;
  entity: string;
  entity_id: string | null;
  details: unknown;
}

export function useAudit(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['admin', 'audit', schoolId],
    queryFn: async () =>
      must(await db.rpc('admin_audit', { p_school: schoolId, p_limit: 200 })) as AuditRow[],
  });
}
